
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Euro, Plus, Calculator, AlertCircle } from "lucide-react";
import { ClientSubscription } from "@/models/Client";
import { useTranslation } from "react-i18next";
import { useWorkoutTypes } from "@/hooks/useWorkoutTypes";
import { useSubscriptionPayments } from "@/hooks/clients/useSubscriptionPayments";
import { calculateSubscriptionPaymentInfo } from "@/utils/subscriptionPayments";
import PaymentDialog from "./PaymentDialog";

interface SubscriptionPaymentCardProps {
  subscription: ClientSubscription;
  client: any;
}

const SubscriptionPaymentCard: React.FC<SubscriptionPaymentCardProps> = ({
  subscription,
  client,
}) => {
  const { t } = useTranslation();
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const { workoutTypes = [] } = useWorkoutTypes();
  const { data: subscriptionPayments = [] } = useSubscriptionPayments(client.id, subscription.id);

  // Calculate payment information for this subscription
  const paymentInfo = calculateSubscriptionPaymentInfo(subscription, subscriptionPayments);

  const getWorkoutTypeName = (workoutTypeId: string) => {
    const workoutType = workoutTypes.find(wt => wt.id === workoutTypeId);
    return workoutType?.name || t('clients.unknownWorkoutType');
  };

  // Use the actual subscription payment status from the database
  const subscriptionPaymentStatus = subscription.paymentStatus || 'unpaid';

  // Debug logging for subscription state
  console.log('SubscriptionPaymentCard - Subscription data:', {
    id: subscription.id,
    workoutTypeId: subscription.workoutTypeId,
    paymentStatus: subscriptionPaymentStatus,
    currentCycleSessionsUsed: subscription.currentCycleSessionsUsed,
    sessionsPerMonth: subscription.sessionsPerMonth,
    currentCycleStartDate: subscription.currentCycleStartDate,
    hasConsumedEnoughSessions: subscription.currentCycleSessionsUsed >= subscription.sessionsPerMonth
  });

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'pending':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'unpaid':
      default:
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
    }
  };

  const getPaymentStatusText = (status: string) => {
    switch (status) {
      case 'paid':
        return t('clients.paid');
      case 'partial':
        return t('clients.partial');
      case 'pending':
        return t('clients.pending');
      case 'unpaid':
      default:
        return t('clients.unpaid');
    }
  };

  const hasConsumedEnoughSessions = subscription.currentCycleSessionsUsed >= subscription.sessionsPerMonth;

  return (
    <>
      <Card className="w-full">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm md:text-base greek-text">
              {getWorkoutTypeName(subscription.workoutTypeId)}
            </CardTitle>
            <Badge className={getPaymentStatusColor(subscriptionPaymentStatus)}>
              {getPaymentStatusText(subscriptionPaymentStatus)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 gap-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('clients.sessionsPerMonth')}:</span>
              <span className="font-medium">{subscription.sessionsPerMonth}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('clients.sessionsUsed')} (τρέχων κύκλος):</span>
              <span className={`font-medium ${hasConsumedEnoughSessions ? 'text-orange-600 dark:text-orange-400' : ''}`}>
                {subscription.currentCycleSessionsUsed}/{subscription.sessionsPerMonth}
                {hasConsumedEnoughSessions && <span className="ml-1">⚠</span>}
              </span>
            </div>
            
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Έναρξη κύκλου:</span>
              <span>{new Date(subscription.currentCycleStartDate).toLocaleDateString('el-GR')}</span>
            </div>

            <div className="flex items-center justify-between border-t pt-2">
              <div className="flex items-center gap-2">
                <Calculator className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground font-medium">{t('clients.monthlyAmount')}:</span>
              </div>
              <div className="flex items-center gap-1">
                <Euro className="h-4 w-4 text-primary" />
                <span className="font-bold text-primary">€{subscription.price.toFixed(2)}</span>
              </div>
            </div>

            {/* Show remaining amount for partial payments */}
            {paymentInfo.isPartial && (
              <div className="flex items-center justify-between bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded border border-yellow-200 dark:border-yellow-600">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                  <span className="text-yellow-700 dark:text-yellow-300 font-medium text-sm">
                    Υπολειπόμενο ποσό:
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Euro className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                  <span className="font-bold text-yellow-700 dark:text-yellow-300">
                    €{paymentInfo.remainingAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {hasConsumedEnoughSessions && subscriptionPaymentStatus !== 'paid' && (
              <div className="text-xs text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 p-2 rounded">
                Η συνδρομή έχει συμπληρώσει τον κύκλο συνεδριών και απαιτεί πληρωμή για έναρξη νέου κύκλου.
              </div>
            )}
          </div>

          <div className="pt-2">
            <Button
              onClick={() => setShowPaymentDialog(true)}
              className="w-full flex items-center gap-2"
              size="sm"
              variant={hasConsumedEnoughSessions && subscriptionPaymentStatus === 'unpaid' ? "destructive" : "default"}
            >
              <Plus className="h-4 w-4" />
              {t('clients.recordPayment')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <PaymentDialog
        open={showPaymentDialog}
        onOpenChange={setShowPaymentDialog}
        clientId={client.id}
        clientName={`${client.firstName} ${client.lastName}`}
        client={client}
        subscription={subscription}
      />
    </>
  );
};

export default SubscriptionPaymentCard;
